import React from 'react';
import clsx from 'clsx';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import { cardSx } from '@elevator/ui';
import { AuthImageSrc } from 'config/staticAssets';
import { Vendor } from 'types/organization';
import useStyles from './styles';

export type VendorCardProps =
  | {
      vendor: Vendor;
      /** Called when the card is clicked (required when interactive). */
      onClick: () => void;
      interactive?: true;
      imagePriority?: boolean;
    }
  | {
      vendor: Vendor;
      onClick?: undefined;
      /** Display-only card (no button semantics or hover affordance). */
      interactive: false;
      imagePriority?: boolean;
    };

/**
 * VendorCard displays a single vendor/organization as a selectable card with
 * banner image and name. Used in VendorsStep for the submission flow.
 * If `vendor.banner?.sourceUrl` is missing, uses `AuthImageSrc` (same imgix asset as profile banner).
 */
const VendorCard = (props: VendorCardProps) => {
  const { vendor, imagePriority = false } = props;
  const interactive = props.interactive !== false;
  const onClick = interactive ? props.onClick : undefined;
  const { classes } = useStyles();

  const customBannerUrl = vendor.banner?.sourceUrl?.trim();
  const bannerSrc = customBannerUrl || AuthImageSrc.src;
  const unoptimized = Boolean(
    customBannerUrl && customBannerUrl.toLowerCase().endsWith('.svg'),
  );

  return (
    <Paper
      square
      elevation={1}
      data-component="VendorCard"
      sx={cardSx}
      className={clsx(classes.root, interactive && classes.interactive)}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      aria-label={interactive ? `Select ${vendor.name}` : undefined}
    >
      <Box className={classes.bannerContainer}>
        <Image
          className={classes.banner}
          src={bannerSrc}
          alt={customBannerUrl ? `${vendor.name} banner` : ''}
          fill
          sizes="(max-width: 768px) 100vw, 500px"
          style={{ objectFit: 'cover' }}
          priority={imagePriority}
          unoptimized={unoptimized}
        />
      </Box>
      <Typography variant="subtitle1" className={classes.vendorName}>
        {vendor.name}
      </Typography>
    </Paper>
  );
};

export default VendorCard;
